import { useState, useEffect, useRef } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import ReactPlayer from 'react-player';
import Loader from '../images/tb-video-loader.gif';
import Failure from '../images/tb-video-failure.gif';
import { Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import * as theme from '../theme';

const VideoPlayer = ({ videoId, isPublic }) => {
  const { currentUser } = useAuth();
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState(null);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const playerRef = useRef(null);
  const lastSavedTimeRef = useRef(0);

  // Fetch video URL from Firebase Storage
  useEffect(() => {
    if (!isPublic && !currentUser) {
      setError('You must be logged in to view this video.');
      return;
    }

    const fetchVideoUrl = async () => {
      try {
        const folder = isPublic ? 'public' : 'private';
        const storage = getStorage();
        const videoRef = ref(storage, `videos/${folder}/${videoId}.mp4`);
        const url = await getDownloadURL(videoRef);
        setVideoUrl(url);
      } catch (err) {
        console.error('Error fetching video URL:', err);
        setError('Failed to load video.');
        setVideoUrl('');
      }
    };

    fetchVideoUrl();
  }, [videoId, isPublic, currentUser]);

  // Load saved playback position from localStorage
  useEffect(() => {
    const savedTime = localStorage.getItem(`video_playback_${videoId}`);
    if (savedTime) {
      setPlayedSeconds(parseFloat(savedTime));
    }
  }, [videoId]);

  // Seek to saved position when video URL is loaded
  useEffect(() => {
    if (playerRef.current && videoUrl && playedSeconds > 0) {
      playerRef.current.seekTo(playedSeconds, 'seconds');
    }
  }, [videoUrl, playedSeconds]);

  // Save playback position every 5 seconds
  const handleProgress = ({ playedSeconds }) => {
    const currentTime = Math.floor(playedSeconds);
    if (currentTime !== lastSavedTimeRef.current && currentTime % 5 === 0) {
      localStorage.setItem(`video_playback_${videoId}`, currentTime);
      lastSavedTimeRef.current = currentTime;
    }
  };

  // Clear saved position when video ends
  const handleEnded = () => {
    localStorage.removeItem(`video_playback_${videoId}`);
    setPlayedSeconds(0);
  };

  return (
    <Box
      sx={{
        maxWidth: '800px',
        mx: 'auto',
        mt: 2,
        // Reserve space with aspect ratio
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9', // Standard video aspect ratio
        bgcolor: isPublic ? theme.Colors.GrayDark : theme.Colors.White, // Black background for loading state
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {videoUrl ? (
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          controls
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
          onProgress={handleProgress}
          onEnded={handleEnded}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
              },
            },
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <img
            src={error ? Failure : Loader}
            alt="Loading video"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain', // Ensure loader fits within container
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoPlayer;